import React, { useEffect, useRef } from "react";
import { useTranslation } from "@helpers/translate";
import "./LoginVerification.sass";

const VERIFICATION_CODE_LENGTH = 6;

const LoginVerification = ({
  email,
  verificationType,
  verificationCode,
  error,
  loading,
  resendLoading,
  resendSuccess,
  onCodeChange,
  onVerify,
  onResend,
}) => {
  const { __ } = useTranslation();

  const inputRefs = useRef([]);
  const lastSubmittedCodeRef = useRef("");

  const getInputLabel = () => {
    switch (verificationType) {
      case "app":
        return __("auth.enter_google_code");

      case "sms":
        return __("auth.enter_sms_code");

      default:
        return __("auth.enter_email_code");
    }
  };

  const isEmailVerification =
    !verificationType || verificationType === "email";

  const codeDigits = Array.from(
    { length: VERIFICATION_CODE_LENGTH },
    (_, index) => verificationCode[index] || "",
  );

  const emitCodeChange = (value) => {
    onCodeChange({
      target: {
        value,
      },
    });
  };

  const focusInput = (index) => {
    const targetIndex = Math.max(
      0,
      Math.min(index, VERIFICATION_CODE_LENGTH - 1),
    );

    inputRefs.current[targetIndex]?.focus();
  };

  const handleDigitChange = (index, event) => {
    const enteredValue = event.target.value.replace(/\D/g, "");

    if (!enteredValue) {
      const nextDigits = [...codeDigits];

      nextDigits[index] = "";

      emitCodeChange(nextDigits.join(""));
      return;
    }

    if (enteredValue.length > 1) {
      const pastedDigits = enteredValue
        .slice(0, VERIFICATION_CODE_LENGTH - index)
        .split("");

      const nextDigits = [...codeDigits];

      pastedDigits.forEach((digit, digitIndex) => {
        nextDigits[index + digitIndex] = digit;
      });

      const nextCode = nextDigits.join("").slice(0, VERIFICATION_CODE_LENGTH);

      emitCodeChange(nextCode);

      const nextFocusIndex = Math.min(
        index + pastedDigits.length,
        VERIFICATION_CODE_LENGTH - 1,
      );

      window.requestAnimationFrame(() => {
        focusInput(nextFocusIndex);
      });

      return;
    }

    const nextDigits = [...codeDigits];

    nextDigits[index] = enteredValue;
    emitCodeChange(nextDigits.join(""));

    if (index < VERIFICATION_CODE_LENGTH - 1) {
      window.requestAnimationFrame(() => {
        focusInput(index + 1);
      });
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      if (codeDigits[index]) {
        const nextDigits = [...codeDigits];

        nextDigits[index] = "";
        emitCodeChange(nextDigits.join(""));
        return;
      }

      if (index > 0) {
        event.preventDefault();

        const nextDigits = [...codeDigits];

        nextDigits[index - 1] = "";
        emitCodeChange(nextDigits.join(""));

        window.requestAnimationFrame(() => {
          focusInput(index - 1);
        });
      }

      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
      return;
    }

    if (
      event.key === "ArrowRight" &&
      index < VERIFICATION_CODE_LENGTH - 1
    ) {
      event.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();

    const pastedCode = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, VERIFICATION_CODE_LENGTH);

    if (!pastedCode) {
      return;
    }

    emitCodeChange(pastedCode);

    const nextFocusIndex = Math.min(
      pastedCode.length,
      VERIFICATION_CODE_LENGTH - 1,
    );

    window.requestAnimationFrame(() => {
      focusInput(nextFocusIndex);
    });
  };

  useEffect(() => {
    focusInput(0);
  }, []);

  useEffect(() => {
    if (verificationCode.length < VERIFICATION_CODE_LENGTH) {
      lastSubmittedCodeRef.current = "";
      return;
    }

    if (
      verificationCode.length === VERIFICATION_CODE_LENGTH &&
      !loading &&
      !error &&
      lastSubmittedCodeRef.current !== verificationCode
    ) {
      lastSubmittedCodeRef.current = verificationCode;
      onVerify();
    }
  }, [verificationCode, loading, error, onVerify]);

  useEffect(() => {
    if (error && !verificationCode) {
      window.requestAnimationFrame(() => {
        focusInput(0);
      });
    }
  }, [error, verificationCode]);

  return (
    <div className="login-verification">
      <h1 className="login-verification__title">
        {__("auth.enter_confirmation_code")}
      </h1>

      {isEmailVerification ? (
        <p className="login-verification__description">
          {__("auth.verification_code_sent")}{" "}
          <span className="login-verification__email">{email}</span>.{" "}
          {__("auth.enter_code_below")}
        </p>
      ) : (
        <p className="login-verification__description">
          {getInputLabel()}
        </p>
      )}

      <div
        className={`login-verification__code${
          error ? " login-verification__code--error" : ""
        }`}
        onPaste={handlePaste}
        role="group"
        aria-label={getInputLabel()}
      >
        {codeDigits.map((digit, index) => (
          <input
            key={index}
            ref={(element) => {
              inputRefs.current[index] = element;
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digit}
            disabled={loading}
            autoComplete={index === 0 ? "one-time-code" : "off"}
            aria-label={`${getInputLabel()} ${index + 1}`}
            aria-invalid={Boolean(error)}
            className={`login-verification__code-input${
              digit ? " login-verification__code-input--filled" : ""
            }${error ? " login-verification__code-input--error" : ""}`}
            onChange={(event) => handleDigitChange(index, event)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onFocus={(event) => event.target.select()}
          />
        ))}
      </div>

      {error ? (
        <p className="login-verification__error" role="alert">
          {error}
        </p>
      ) : (
        <div className="login-verification__error-placeholder" />
      )}

      {isEmailVerification && (
        <div className="login-verification__resend">
          <span className="login-verification__resend-text">
            {__("auth.did_not_receive_email")}
          </span>

          <button
            type="button"
            className="login-verification__resend-button"
            onClick={onResend}
            disabled={resendLoading || loading}
          >
            {resendLoading
              ? __("auth.resending_code")
              : __("auth.resend_code")}
          </button>

          {resendSuccess && (
            <span className="login-verification__resend-success">
              {__("auth.code_resent")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default LoginVerification;
